/**
 * `useAssistantIdentity` — the shared read side of the §6.2 assistant record.
 *
 * One place to `identity.get` + re-read on `identity.updated`, so the hero, the
 * channel summary and (later) the chat header all show the same live name /
 * avatar without each re-implementing the fetch. WRITES stay in the identity
 * editor (`IdentityLayout` today; the S3 hero picker next) — this hook is
 * read-only.
 *
 * `backendPending` mirrors `IdentityLayout`'s method-not-found handling: a
 * runtime that predates `identity.get` should show the graceful "Alex" default,
 * not an error.
 */
import { useCallback, useEffect, useState } from "react";

import { useGatewayStore } from "@/stores/useGatewayStore";
import { useLexEventStream } from "@/hooks/useEventStream";
import { ASSISTANT_DEFAULT_NAME } from "@/lib/contacts-logic";
import type { AssistantIdentity, Contact } from "@/types/contacts";

/** Platform default assistant name (operator 2026-07-27: the hero must read
 *  "Alex", never the brand). Defined in the pure-logic module so the previews
 *  there and this hook cannot drift apart; re-exported for existing callers. */
export { ASSISTANT_DEFAULT_NAME };

/** Names the runtime historically provisioned as its OWN default ("Lexiun",
 *  assistant-identity-store.ts:90). A record carrying one of these was never
 *  actually named by the lawyer, so the SPA maps it to the platform default —
 *  the display-side half of the §9.3a "Alex platform-wide" handoff (the runtime
 *  fallback + lexserver From-grammar halves are queued on the bus). */
const LEGACY_DEFAULT_NAMES = new Set(["lexiun"]);

/** True when the assistant was never really named — empty or a legacy runtime
 *  default. The DEFAULT identity gets the full default look: the name "Alex"
 *  AND Alex's human default avatar (even over a seeded emoji). */
export function isDefaultAssistantIdentity(identity: AssistantIdentity | null): boolean {
  const n = identity?.name?.trim().toLowerCase();
  return !n || LEGACY_DEFAULT_NAMES.has(n);
}

export function assistantDisplayName(identity: AssistantIdentity | null): string {
  return isDefaultAssistantIdentity(identity)
    ? ASSISTANT_DEFAULT_NAME
    : (identity as AssistantIdentity).name.trim();
}

/** Initials for the broken-custom-URL fallback roundel (never blank). */
export function assistantInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "A";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function methodPending(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /method|unknown|not.?found|unsupported/i.test(msg);
}

export interface AssistantIdentityState {
  assistant: AssistantIdentity | null;
  owner: Contact | null;
  loading: boolean;
  /** The runtime predates `identity.get` — show the default, not an error. */
  backendPending: boolean;
  refresh: () => void;
}

export function useAssistantIdentity(): AssistantIdentityState {
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const [assistant, setAssistant] = useState<AssistantIdentity | null>(null);
  const [owner, setOwner] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [backendPending, setBackendPending] = useState(false);

  const refresh = useCallback(() => {
    if (!rpc || connectionState !== "connected") return;
    rpc
      .identityGet()
      .then((res) => {
        setAssistant(res.assistant);
        setOwner(res.owner);
        setBackendPending(false);
      })
      .catch((err: unknown) => {
        if (methodPending(err)) setBackendPending(true);
      })
      .finally(() => setLoading(false));
  }, [rpc, connectionState]);

  useEffect(() => refresh(), [refresh]);
  useLexEventStream("identity.updated", () => refresh());

  return { assistant, owner, loading, backendPending, refresh };
}
